/**
 * BoardDetailPanel: Product-grade task detail panel for the Sakme board.
 *
 * SAKME-BOARD-1 punch list item 1: Extends the original TaskDetailPanel with:
 * - Dependency DAG with live status icons and click-to-navigate
 * - Event timeline styled as a vertical timeline
 * - Comment thread UI (ready for backend, shows empty state)
 * - Contextual action buttons based on task status
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  X,
  Link2,
  FileText,
  Clock,
  User,
  ArrowRight,
  AlertTriangle,
  CheckCircle2,
  Loader2,
  GitBranch,
  Shield,
  ShieldCheck,
  ShieldAlert,
  ShieldX,
  Hourglass,
  Play,
  Send,
  RotateCcw,
  Ban,
  Activity,
  Circle,
  Archive,
  Copy,
  Check,
  Bot,
  ExternalLink,
  GitPullRequest,
  Workflow,
} from 'lucide-react';
import api from '@/api/engine';
import { listAgentRunsForTask } from '@/api/agentTasks';
import type {
  TaskItem,
  TaskStatus,
  TaskPriority,
  TaskEvent,
} from '@/api/types';
import type { AgentRunRead } from '@/api/agentBoardTypes';
import { agentCompletionPolicy, getWorkflowStage, tagsWithPrefix } from '@/lib/agentWorkflow';
import { TaskCommentThread } from './TaskCommentThread';
import { RulingSubmitModal } from './RulingSubmitModal';

// ── Status & Priority config ────────────────────────────

const STATUS_OPTIONS: { value: TaskStatus; label: string; color: string }[] = [
  { value: 'backlog', label: 'Backlog', color: '#6b7280' },
  { value: 'todo', label: 'To Do', color: '#3b82f6' },
  { value: 'in_progress', label: 'In Progress', color: 'var(--theme-accent-secondary, #39FF14)' },
  { value: 'blocked', label: 'Blocked', color: 'var(--theme-danger, #ef4444)' },
  { value: 'review', label: 'Review', color: 'var(--theme-warning, #facc15)' },
  { value: 'done', label: 'Done', color: 'var(--theme-success, #22c55e)' },
  { value: 'cancelled', label: 'Cancelled', color: '#4b5563' },
];

const PRIORITY_OPTIONS: { value: TaskPriority; label: string; color: string }[] = [
  { value: 'critical', label: 'Critical', color: 'var(--theme-danger, #ef4444)' },
  { value: 'high', label: 'High', color: '#f97316' },
  { value: 'medium', label: 'Medium', color: 'var(--theme-warning, #eab308)' },
  { value: 'low', label: 'Low', color: '#6b7280' },
];

const GOVERNOR_ICON: Record<string, { Icon: typeof Shield; color: string; label: string }> = {
  pending:           { Icon: Hourglass,  color: 'text-gray-400',   label: 'Pending' },
  passed:            { Icon: ShieldCheck, color: 'text-green-400',  label: 'Passed' },
  held_for_review:   { Icon: ShieldAlert, color: 'text-yellow-300', label: 'Held for review' },
  failed:            { Icon: ShieldX,    color: 'text-red-400',    label: 'Failed' },
  director_override: { Icon: Shield,     color: 'text-blue-400',   label: 'Director override' },
};

// ── Event timeline helpers ──────────────────────────────

const EVENT_ICON: Record<string, { icon: typeof Activity; color: string }> = {
  task_created:          { icon: Circle,       color: 'text-gray-400' },
  task_dispatched:       { icon: Send,         color: 'text-[#39FF14]' },
  task_status_changed:   { icon: ArrowRight,   color: 'text-cyan-400' },
  task_completed:        { icon: CheckCircle2, color: 'text-green-400' },
  task_held_for_review:  { icon: ShieldAlert,  color: 'text-yellow-300' },
  task_blocked:          { icon: Ban,          color: 'text-red-400' },
  task_cancelled:        { icon: X,            color: 'text-gray-500' },
  task_stopped:          { icon: Ban,          color: 'text-gray-500' },
};

function parseSseLine(raw: string): TaskEvent | null {
  const trimmed = raw.trim();
  if (!trimmed.startsWith('data:')) return null;
  const payload = trimmed.slice(5).trim();
  if (!payload) return null;
  try {
    return JSON.parse(payload) as TaskEvent;
  } catch {
    try {
      const coerced = payload
        .replace(/'/g, '"')
        .replace(/\bNone\b/g, 'null')
        .replace(/\bTrue\b/g, 'true')
        .replace(/\bFalse\b/g, 'false');
      return JSON.parse(coerced) as TaskEvent;
    } catch {
      return null;
    }
  }
}

function formatEventTime(iso: string): string {
  try {
    return new Date(iso).toLocaleTimeString('en-US', { hour12: false });
  } catch {
    return iso;
  }
}

// ── Dependency status icon ──────────────────────────────

function DepStatusIcon({ status }: { status: TaskStatus }) {
  switch (status) {
    case 'done':
      return <CheckCircle2 className="w-3.5 h-3.5 text-green-500 shrink-0" />;
    case 'in_progress':
      return <Play className="w-3.5 h-3.5 text-[#39FF14] shrink-0" />;
    case 'blocked':
      return <Ban className="w-3.5 h-3.5 text-red-400 shrink-0" />;
    case 'review':
      return <ShieldAlert className="w-3.5 h-3.5 text-yellow-300 shrink-0" />;
    default:
      return <Circle className="w-3.5 h-3.5 text-gray-500 shrink-0" />;
  }
}

// =============================================================================
// DispatchPromptBlock — readable + copyable rendering of task.dispatch_prompt
// =============================================================================
//
// Director ruling 2026-05-12: every task should be actionable from the board.
// The dispatch_prompt is the rich text an agent reads when starting work
// (markdown describing scope, files, gates, what-not-to-do, etc.). Showing
// it as a truncated single line defeats the point. Render as a readable
// block that's collapsed-by-default for short prompts and a copy-on-click
// affordance.

function DispatchPromptBlock({ prompt }: { prompt: string | null | undefined }) {
  const [copied, setCopied] = useState(false);
  const [expanded, setExpanded] = useState(false);

  // No prompt: show a small affordance so the user understands what's
  // missing rather than the field just silently not rendering.
  if (!prompt || prompt.trim() === '') {
    return (
      <div
        className="text-[11px] font-mono italic flex items-center gap-2 p-2 rounded-lg border border-dashed"
        style={{
          color: 'var(--theme-text-muted, #8e98ab)',
          borderColor: 'var(--theme-surface-tertiary, #222836)',
        }}
      >
        <FileText className="w-3 h-3 shrink-0" />
        <span>No dispatch prompt attached. (CLI: <code className="text-cyan-400">--prompt-file path/to/prompt.md</code>)</span>
      </div>
    );
  }

  const lineCount = prompt.split('\n').length;
  const isShort = lineCount <= 6 && prompt.length <= 600;
  const previewLines = prompt.split('\n').slice(0, 6).join('\n');

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(prompt);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      // Clipboard API blocked or unavailable; fall back silently.
      // The text is selectable in the visible block anyway.
    }
  };

  return (
    <div
      className="rounded-lg border"
      style={{
        borderColor: 'var(--theme-surface-tertiary, #222836)',
        backgroundColor: 'var(--theme-surface-secondary, rgba(20, 25, 35, 0.4))',
      }}
    >
      <div
        className="flex items-center justify-between px-3 py-2 border-b"
        style={{ borderColor: 'var(--theme-surface-tertiary, #222836)' }}
      >
        <div className="flex items-center gap-2">
          <FileText className="w-3.5 h-3.5 text-cyan-400 shrink-0" />
          <span
            className="text-[10px] font-mono uppercase tracking-wider"
            style={{ color: 'var(--theme-text-muted, #8e98ab)' }}
          >
            Dispatch Prompt ({lineCount} line{lineCount === 1 ? '' : 's'})
          </span>
        </div>
        <div className="flex items-center gap-2">
          {!isShort && (
            <button
              onClick={() => setExpanded((v) => !v)}
              className="text-[10px] font-mono uppercase tracking-wider hover:text-cyan-400 transition-colors"
              style={{ color: 'var(--theme-text-muted, #8e98ab)' }}
              title={expanded ? 'Collapse' : 'Show full prompt'}
            >
              {expanded ? 'Collapse' : 'Expand'}
            </button>
          )}
          <button
            onClick={copyToClipboard}
            className="flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider hover:text-cyan-400 transition-colors"
            style={{ color: copied ? 'var(--theme-success, #39FF14)' : 'var(--theme-text-muted, #8e98ab)' }}
            title="Copy prompt to clipboard"
          >
            {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
            {copied ? 'Copied' : 'Copy'}
          </button>
        </div>
      </div>
      <pre
        className="text-xs font-mono whitespace-pre-wrap break-words p-3 overflow-x-auto"
        style={{ color: 'var(--theme-text-secondary, #c2c9d6)' }}
      >
        {isShort || expanded ? prompt : previewLines + '\n…'}
      </pre>
    </div>
  );
}

// =============================================================================
// SourceLinkBlock — show the memo/doc that originated this task
// =============================================================================
//
// Two patterns supported:
//   1. `source_doc` field — older pattern, populated by hand
//   2. `source_memo:<path>` tag — newer pattern from memo-hook pipeline
//
// Both render as clickable affordances. The links are advisory (the docs
// live in the CES repo, not in seitiate-plus), so they're rendered as
// text the user can copy / search for.

function SourceLinkBlock({
  sourceDoc,
  tags,
}: {
  sourceDoc: string | null | undefined;
  tags: string[] | null | undefined;
}) {
  const sourceMemoTags = (tags ?? []).filter((t) => t.startsWith('source_memo:'));
  const sourceMemoPaths = sourceMemoTags.map((t) => t.slice('source_memo:'.length));

  if (!sourceDoc && sourceMemoPaths.length === 0) {
    return null;
  }

  return (
    <div className="space-y-1">
      <div
        className="text-[10px] font-mono uppercase tracking-wider"
        style={{ color: 'var(--theme-text-muted, #8e98ab)' }}
      >
        Source
      </div>
      <div className="space-y-1">
        {sourceDoc && (
          <div className="flex items-center gap-2">
            <FileText className="w-3 h-3 shrink-0" style={{ color: 'var(--theme-text-muted, #8e98ab)' }} />
            <code
              className="text-xs font-mono break-all"
              style={{ color: 'var(--theme-text-secondary, #c2c9d6)' }}
              title="Path to source document (relative to CES repo root)"
            >
              {sourceDoc}
            </code>
          </div>
        )}
        {sourceMemoPaths.map((path) => (
          <div key={path} className="flex items-center gap-2">
            <Link2 className="w-3 h-3 shrink-0 text-cyan-500" />
            <code
              className="text-xs font-mono break-all text-cyan-300"
              title="Sovereign memo or brainstorming doc that justified this task (relative to CES repo root)"
            >
              {path}
            </code>
          </div>
        ))}
      </div>
    </div>
  );
}

// =============================================================================
// BoardDetailPanel
// =============================================================================

interface BoardDetailPanelProps {
  task: TaskItem;
  allTasks: TaskItem[];
  onClose: () => void;
  onUpdate: () => void;
  onSelectTask: (task: TaskItem) => void;
  readOnly?: boolean;
}

export function BoardDetailPanel({
  task, allTasks, onClose, onUpdate, onSelectTask, readOnly = false,
}: BoardDetailPanelProps) {
  const [saving, setSaving] = useState(false);
  const [editStatus, setEditStatus] = useState<TaskStatus>(task.status);
  const [editPriority, setEditPriority] = useState<TaskPriority>(task.priority);
  const [editAssigned, setEditAssigned] = useState(task.assigned_to || '');
  const [editDueDate, setEditDueDate] = useState(task.due_date || '');
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [rulingModalOpen, setRulingModalOpen] = useState(false);

  // Director ruling 2026-05-12: RULING-* tasks expose a "Submit Ruling"
  // affordance that opens RulingSubmitModal, which posts to the backend
  // memo-ruling endpoint and closes the memo → board → ruling → unblock
  // loop. We surface the button only when:
  //   1. task_id starts with 'RULING-' (set by memo-hook)
  //   2. task is not already done/cancelled (nothing left to rule)
  //   3. the task has parseable source_memo:* AND blocked_on:* tags
  //      (the modal needs both)
  //   4. not in readOnly mode
  const rulingContext = useMemo(() => {
    if (!task.task_id || !task.task_id.startsWith('RULING-')) return null;
    if (task.status === 'done' || task.status === 'cancelled') return null;
    if (readOnly) return null;
    const tags = task.tags ?? [];
    const sourceMemoTag = tags.find((t) => t.startsWith('source_memo:'));
    const blockedOnTag = tags.find((t) => t.startsWith('blocked_on:'));
    if (!sourceMemoTag || !blockedOnTag) return null;
    return {
      memoPath: sourceMemoTag.slice('source_memo:'.length),
      questionId: blockedOnTag.slice('blocked_on:'.length),
    };
  }, [task.task_id, task.status, task.tags, readOnly]);

  // SSE event stream
  const [events, setEvents] = useState<TaskEvent[]>([]);
  const [sseConnected, setSseConnected] = useState(false);
  const [agentRuns, setAgentRuns] = useState<AgentRunRead[]>([]);
  const [agentRunsLoading, setAgentRunsLoading] = useState(false);
  const [agentRunsError, setAgentRunsError] = useState<string | null>(null);
  const shouldFetchAgentRuns = useMemo(() => {
    const tags = task.tags ?? [];
    return Boolean(task.category?.startsWith('solopreneurs/'))
      || tags.some((tag) =>
        tag.startsWith('workflow:')
        || tag.startsWith('evidence:')
        || tag.startsWith('review-agent:')
        || tag.startsWith('approver:')
        || tag === 'decision-required'
      );
  }, [task.category, task.tags]);

  // Reset on task change
  useEffect(() => {
    setEditStatus(task.status);
    setEditPriority(task.priority);
    setEditAssigned(task.assigned_to || '');
    setEditDueDate(task.due_date || '');
    setActionError(null);
  }, [task.id, task.status, task.priority, task.assigned_to, task.due_date]);

  // SSE subscription for event stream
  useEffect(() => {
    if (!task.harness_workflow_id && task.status !== 'in_progress' && task.status !== 'review' && task.status !== 'blocked') {
      return;
    }
    setEvents([]);
    setSseConnected(false);

    let source: EventSource | null = null;
    try {
      source = api.tasks.subscribeEvents(task.id);
    } catch {
      return;
    }

    source.onopen = () => setSseConnected(true);
    source.onmessage = (ev) => {
      const parsed = parseSseLine(`data: ${ev.data as string}`);
      if (parsed) setEvents((prev) => [...prev, parsed]);
    };
    source.onerror = () => setSseConnected(false);

    return () => { source?.close(); };
  }, [task.id, task.harness_workflow_id, task.status]);

  useEffect(() => {
    let cancelled = false;
    setAgentRuns((prev) => (prev.length > 0 ? [] : prev));
    setAgentRunsError((prev) => (prev ? null : prev));
    if (!shouldFetchAgentRuns) {
      setAgentRunsLoading((prev) => (prev ? false : prev));
      return () => { cancelled = true; };
    }
    setAgentRunsLoading(true);
    listAgentRunsForTask(task.id)
      .then((runs) => {
        if (!cancelled) setAgentRuns(runs);
      })
      .catch((err) => {
        if (!cancelled) {
          setAgentRunsError(err instanceof Error ? err.message : 'Agent runs unavailable');
        }
      })
      .finally(() => {
        if (!cancelled) setAgentRunsLoading(false);
      });
    return () => { cancelled = true; };
  }, [task.id, shouldFetchAgentRuns]);

  const saveField = useCallback(async (field: string, value: string | null) => {
    setSaving(true);
    try {
      await api.tasks.update(task.id, { [field]: value || null } as Partial<TaskItem>);
      onUpdate();
    } finally {
      setSaving(false);
    }
  }, [task.id, onUpdate]);

  // ── Dependency data ─────────────────────────────────
  const findTask = useCallback(
    (taskId: string) => allTasks.find((t) => t.task_id === taskId),
    [allTasks],
  );

  const dependsOnTasks = useMemo(
    () => (task.depends_on || []).map((id) => ({ id, task: findTask(id) })),
    [task.depends_on, findTask],
  );
  const blocksTasks = useMemo(
    () => (task.blocks || []).map((id) => ({ id, task: findTask(id) })),
    [task.blocks, findTask],
  );

  const isOverdue = task.due_date && task.status !== 'done' && task.status !== 'cancelled'
    && new Date(task.due_date) < new Date();
  const workflow = useMemo(() => getWorkflowStage(task), [task]);
  const completionPolicy = useMemo(() => agentCompletionPolicy(task), [task]);
  const evidenceTags = useMemo(() => tagsWithPrefix(task, 'evidence:'), [task]);
  const approverTags = useMemo(() => tagsWithPrefix(task, 'approver:'), [task]);
  const reviewAgentTags = useMemo(() => tagsWithPrefix(task, 'review-agent:'), [task]);
  const gateTags = useMemo(() => {
    const knownGates = new Set([
      'decision-required',
      'legal-review',
      'security-review',
      'money-review',
      'product-review',
      'partner-review',
    ]);
    return (task.tags ?? []).filter((tag) => knownGates.has(tag));
  }, [task]);
  const prRuns = useMemo(() => agentRuns.filter((run) => Boolean(run.pr_url)), [agentRuns]);
  const evidenceRuns = useMemo(() => agentRuns.filter((run) => Boolean(run.evidence_uri)), [agentRuns]);
  const hasWorkflowMetadata = Boolean(workflow.stage)
    || evidenceTags.length > 0
    || approverTags.length > 0
    || reviewAgentTags.length > 0
    || gateTags.length > 0;

  // BOARD-TASK-DETAIL-NO-DELETE-1: gate cancel/archive behind an explicit
  // confirm step. The engine's DELETE /tasks/{id} is a soft cancel (sets
  // status=cancelled, no row deletion) — see docs/TASK-LIFECYCLE.md. This
  // makes the action safer for new dogfooders to invoke without losing data,
  // but the confirm step still keeps it from being a one-click misclick.
  const [cancelConfirm, setCancelConfirm] = useState(false);

  // ── Action handlers ─────────────────────────────────
  const handleAction = useCallback(async (action: string) => {
    setActionLoading(action);
    setActionError(null);
    try {
      switch (action) {
        case 'dispatch':
          await api.tasks.dispatch(task.id, { agent_type: 'developer' });
          break;
        case 'approve':
          await api.tasks.approve(task.id, { reason: 'Director override' });
          break;
        case 'complete':
          await api.tasks.complete(task.id);
          break;
        case 'activate':
          await api.tasks.update(task.id, { status: 'in_progress' } as Partial<TaskItem>);
          break;
        case 'block':
          await api.tasks.update(task.id, { status: 'blocked' } as Partial<TaskItem>);
          break;
        case 'requeue':
          await api.tasks.update(task.id, { status: 'todo' } as Partial<TaskItem>);
          break;
        case 'cancel':
          // Soft cancel via DELETE — engine flips status to cancelled.
          // Cancelled tasks are hidden by the default board filter but the
          // row remains in the DB, recoverable by Reopen below.
          await api.tasks.remove(task.id);
          setCancelConfirm(false);
          break;
        case 'reopen':
          await api.tasks.update(task.id, { status: 'todo' } as Partial<TaskItem>);
          break;
      }
      onUpdate();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : `${action} failed`);
    } finally {
      setActionLoading(null);
    }
  }, [task.id, onUpdate]);

  // Reset confirm state when the user navigates between tasks.
  useEffect(() => {
    setCancelConfirm(false);
  }, [task.id]);

  // ── Which action buttons to show ────────────────────
  const actions = useMemo(() => {
    const result: { id: string; label: string; icon: typeof Play; className: string }[] = [];
    switch (task.status) {
      case 'backlog':
      case 'todo':
        result.push(
          { id: 'dispatch', label: 'Dispatch', icon: Send, className: 'border-[#39FF14]/50 text-[#39FF14] hover:bg-[#39FF14]/10' },
          { id: 'activate', label: 'Activate', icon: Play, className: 'border-blue-500/50 text-blue-400 hover:bg-blue-500/10' },
        );
        break;
      case 'in_progress':
        result.push(
          { id: 'complete', label: 'Complete', icon: CheckCircle2, className: 'border-green-500/50 text-green-400 hover:bg-green-500/10' },
          { id: 'block', label: 'Block', icon: Ban, className: 'border-red-500/50 text-red-400 hover:bg-red-500/10' },
        );
        break;
      case 'blocked':
        result.push(
          { id: 'requeue', label: 'Requeue', icon: RotateCcw, className: 'border-blue-500/50 text-blue-400 hover:bg-blue-500/10' },
          { id: 'activate', label: 'Activate', icon: Play, className: 'border-[#39FF14]/50 text-[#39FF14] hover:bg-[#39FF14]/10' },
        );
        break;
      case 'review':
        result.push(
          { id: 'approve', label: 'Approve', icon: ShieldCheck, className: 'border-green-500/50 text-green-400 hover:bg-green-500/10' },
          { id: 'block', label: 'Block', icon: Ban, className: 'border-red-500/50 text-red-400 hover:bg-red-500/10' },
          { id: 'activate', label: 'Back to Active', icon: RotateCcw, className: 'border-blue-500/50 text-blue-400 hover:bg-blue-500/10' },
        );
        break;
      case 'done':
        result.push(
          { id: 'requeue', label: 'Reopen', icon: RotateCcw, className: 'border-yellow-500/50 text-yellow-400 hover:bg-yellow-500/10' },
        );
        break;
      case 'cancelled':
        result.push(
          { id: 'reopen', label: 'Reopen', icon: RotateCcw, className: 'border-yellow-500/50 text-yellow-400 hover:bg-yellow-500/10' },
        );
        break;
    }
    return result;
  }, [task.status]);

  // Cancel/archive is a separate, deliberately-prominent affordance.
  // Hidden when the task is already terminal (done/cancelled) — there's
  // nothing meaningful to cancel.
  const canCancel = task.status !== 'cancelled' && task.status !== 'done';

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* ─── Header ────────────────────────────────── */}
      <div
        className="flex items-center justify-between px-5 py-3.5 border-b"
        style={{
          borderColor: 'var(--theme-surface-tertiary, #222836)',
          backgroundColor: 'var(--theme-surface-secondary, #1a1f2a)',
        }}
      >
        <div className="flex items-center gap-2.5 min-w-0">
          {task.task_id && (
            <span
              className="text-xs font-mono font-bold shrink-0"
              style={{ color: 'var(--theme-accent-primary, var(--sp-accent))' }}
            >
              {task.task_id}
            </span>
          )}
          <h2
            className="text-sm font-semibold truncate"
            style={{ color: 'var(--theme-text-primary, #f4f7fb)' }}
          >
            {task.title}
          </h2>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {saving && <Loader2 className="w-3.5 h-3.5 text-[#39FF14] animate-spin" />}
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-white/5 transition-colors"
          >
            <X className="w-4 h-4" style={{ color: 'var(--theme-text-muted, #8e98ab)' }} />
          </button>
        </div>
      </div>

      {/* ─── Body ──────────────────────────────────── */}
      <div className="flex-1 overflow-y-auto">
        {/* Action buttons strip — render only when something is actually
            interactive. `canCancel && !readOnly` covers the standalone
            "Cancel & archive" affordance; `actions.length > 0` covers
            status-driven buttons (which are themselves visible regardless
            of readOnly today, e.g. Reopen on a done task). Avoids an empty
            strip in any future state where neither fires. */}
        {(actions.length > 0 || (canCancel && !readOnly) || rulingContext) && (
          <div
            className="flex items-center gap-2 px-5 py-3 border-b flex-wrap"
            style={{ borderColor: 'var(--theme-surface-tertiary, #222836)' }}
            data-testid="board-detail-action-strip"
          >
            {/* Director ruling — only present for RULING-* tasks with parseable
                source_memo + blocked_on tags. Renders before status actions so
                it's the primary call-to-action when a ruling is pending. */}
            {rulingContext && (
              <button
                onClick={() => setRulingModalOpen(true)}
                data-testid="board-detail-action-submit-ruling"
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold border border-cyan-500/60 text-cyan-200 bg-cyan-900/20 hover:bg-cyan-900/40 transition-colors"
                title="Open the Director Ruling modal — submits decision + ruling_text to the memo-ruling endpoint, marks this task done, unblocks downstream tasks tagged blocked_on:{questionId}"
              >
                <Send className="w-3.5 h-3.5" />
                Submit Director Ruling
              </button>
            )}

            {actions.map((action) => {
              const Icon = action.icon;
              const isLoading = actionLoading === action.id;
              return (
                <button
                  key={action.id}
                  onClick={() => void handleAction(action.id)}
                  disabled={!!actionLoading}
                  data-testid={`board-detail-action-${action.id}`}
                  className={`
                    flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold
                    border transition-colors disabled:opacity-40 disabled:cursor-not-allowed
                    ${action.className}
                  `}
                >
                  {isLoading
                    ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    : <Icon className="w-3.5 h-3.5" />
                  }
                  {action.label}
                </button>
              );
            })}

            {/* Cancel / Archive — soft cancel only, see TASK-LIFECYCLE.md.
                Hidden in read-only mode and on terminal tasks. */}
            {canCancel && !readOnly && (
              cancelConfirm ? (
                <div
                  className="flex items-center gap-2 ml-auto"
                  data-testid="board-detail-cancel-confirm"
                >
                  <span className="text-[10px] font-mono text-gray-400 italic">
                    Cancel & archive this task? Soft cancel — recoverable via Reopen.
                  </span>
                  <button
                    onClick={() => void handleAction('cancel')}
                    disabled={!!actionLoading}
                    data-testid="board-detail-action-cancel-confirm"
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold border border-red-500/60 text-red-300 bg-red-900/20 hover:bg-red-900/40 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    {actionLoading === 'cancel'
                      ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      : <Archive className="w-3.5 h-3.5" />
                    }
                    Yes, cancel & archive
                  </button>
                  <button
                    onClick={() => setCancelConfirm(false)}
                    disabled={!!actionLoading}
                    className="px-2 py-1.5 text-xs font-mono text-gray-400 hover:text-gray-200 transition-colors disabled:opacity-40"
                  >
                    Keep
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => setCancelConfirm(true)}
                  disabled={!!actionLoading}
                  data-testid="board-detail-action-cancel"
                  className="ml-auto flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border border-gray-700/70 text-gray-400 hover:border-red-500/50 hover:text-red-300 hover:bg-red-900/10 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                  title="Soft cancel: status → cancelled. Hard delete is not supported."
                >
                  <Archive className="w-3.5 h-3.5" />
                  Cancel & archive
                </button>
              )
            )}

            {actionError && (
              <span className="text-[11px] text-red-400 w-full mt-1">{actionError}</span>
            )}
          </div>
        )}

        <div className="p-5 space-y-5">
          {/* Status + Priority + Assigned + Due */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] font-mono uppercase mb-1 block" style={{ color: 'var(--theme-text-muted, #8e98ab)' }}>
                Status
              </label>
              <select
                value={editStatus}
                onChange={(e) => {
                  const val = e.target.value as TaskStatus;
                  setEditStatus(val);
                  saveField('status', val);
                }}
                className="w-full rounded-lg px-2.5 py-1.5 text-xs font-mono focus:outline-none focus:ring-1 focus:ring-[var(--sp-accent)]/50"
                style={{
                  backgroundColor: 'var(--theme-surface-tertiary, #222836)',
                  borderColor: 'var(--theme-surface-tertiary, #334155)',
                  color: 'var(--theme-text-primary, #f4f7fb)',
                }}
              >
                {STATUS_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-[10px] font-mono uppercase mb-1 block" style={{ color: 'var(--theme-text-muted, #8e98ab)' }}>
                Priority
              </label>
              <select
                value={editPriority}
                onChange={(e) => {
                  const val = e.target.value as TaskPriority;
                  setEditPriority(val);
                  saveField('priority', val);
                }}
                className="w-full rounded-lg px-2.5 py-1.5 text-xs font-mono focus:outline-none focus:ring-1 focus:ring-[var(--sp-accent)]/50"
                style={{
                  backgroundColor: 'var(--theme-surface-tertiary, #222836)',
                  color: 'var(--theme-text-primary, #f4f7fb)',
                }}
              >
                {PRIORITY_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-[10px] font-mono uppercase mb-1 flex items-center gap-1" style={{ color: 'var(--theme-text-muted, #8e98ab)' }}>
                <User className="w-3 h-3" /> Assigned
              </label>
              <input
                type="text"
                value={editAssigned}
                onChange={(e) => setEditAssigned(e.target.value)}
                onBlur={() => saveField('assigned_to', editAssigned)}
                placeholder="Unassigned"
                className="w-full rounded-lg px-2.5 py-1.5 text-xs font-mono placeholder:text-gray-600 focus:outline-none focus:ring-1 focus:ring-[var(--sp-accent)]/50"
                style={{
                  backgroundColor: 'var(--theme-surface-tertiary, #222836)',
                  color: 'var(--theme-text-primary, #f4f7fb)',
                }}
              />
            </div>
            <div>
              <label className={`text-[10px] font-mono uppercase mb-1 flex items-center gap-1 ${isOverdue ? 'text-red-400' : ''}`} style={isOverdue ? undefined : { color: 'var(--theme-text-muted, #8e98ab)' }}>
                <Clock className="w-3 h-3" /> Due Date
                {isOverdue && <AlertTriangle className="w-3 h-3" />}
              </label>
              <input
                type="date"
                data-testid="board-detail-due-date-input"
                value={editDueDate}
                onChange={(e) => {
                  setEditDueDate(e.target.value);
                  saveField('due_date', e.target.value);
                }}
                className="w-full rounded-lg px-2.5 py-1.5 text-xs font-mono focus:outline-none focus:ring-1 focus:ring-[var(--sp-accent)]/50"
                style={{
                  backgroundColor: 'var(--theme-surface-tertiary, #222836)',
                  color: 'var(--theme-text-primary, #f4f7fb)',
                }}
              />
              {/*
                BOARD-DATE-VISIBILITY-1: when no due date is set, surface that
                fact explicitly (instead of showing only an empty <input>).
                Created/Updated timestamps remain at the bottom of the panel.
              */}
              {!editDueDate && (
                <div
                  data-testid="board-detail-no-due-date"
                  className="mt-1 text-[10px] font-mono italic"
                  style={{ color: 'var(--theme-text-muted, #8e98ab)' }}
                >
                  Due date: No due date — this task is unscheduled.
                </div>
              )}
            </div>
          </div>

          {/* Tags + category */}
          <div className="flex flex-wrap gap-1.5">
            {task.category && (
              <span className="text-[10px] font-mono px-2 py-0.5 rounded-md bg-cyan-900/20 text-cyan-400 border border-cyan-800/30">
                {task.category}
              </span>
            )}
            {task.tier > 0 && (
              <span className="text-[10px] font-mono px-2 py-0.5 rounded-md" style={{ backgroundColor: 'var(--theme-surface-tertiary, #222836)', color: 'var(--theme-text-muted, #8e98ab)' }}>
                Tier {task.tier}
              </span>
            )}
            {(task.tags || []).map((tag) => (
              <span key={tag} className="text-[10px] font-mono px-2 py-0.5 rounded-md" style={{ backgroundColor: 'var(--theme-surface-tertiary, #222836)', color: 'var(--theme-text-muted, #8e98ab)' }}>
                {tag}
              </span>
            ))}
          </div>

          {/* Agent-managed workflow metadata (AGENT-MANAGED-WORKING-BOARD-V1).
              This remains tag/field-based in V1, but makes the lifecycle and
              evidence gates explicit for humans reading the board. */}
          {(hasWorkflowMetadata || agentRuns.length > 0 || agentRunsError) && (
            <div
              className="rounded-lg border p-3.5 space-y-3"
              style={{
                borderColor: 'var(--theme-surface-tertiary, #222836)',
                backgroundColor: 'rgba(0,0,0,0.15)',
              }}
              data-testid="board-detail-agent-workflow"
            >
              <label className="text-[10px] font-mono uppercase flex items-center gap-1.5" style={{ color: 'var(--theme-text-muted, #8e98ab)' }}>
                <Workflow className="w-3 h-3" /> Agent Workflow
              </label>

              <div className="flex flex-wrap gap-1.5">
                {workflow.stage && (
                  <span
                    className="inline-flex items-center gap-1 text-[10px] font-mono px-2 py-0.5 rounded-md"
                    style={{ color: workflow.stage.color, backgroundColor: workflow.stage.bg }}
                    title={workflow.hasConflict ? `Multiple workflow tags: ${workflow.workflowTags.join(', ')}` : workflow.stage.label}
                  >
                    {workflow.stage.label}
                    {workflow.hasConflict ? ' · tag conflict' : ''}
                  </span>
                )}
                <span
                  className={`text-[10px] font-mono px-2 py-0.5 rounded-md ${
                    completionPolicy === 'blocked'
                      ? 'bg-rose-950/30 text-rose-300'
                      : completionPolicy === 'allowed'
                        ? 'bg-green-950/30 text-green-300'
                        : 'bg-gray-800/60 text-gray-400'
                  }`}
                >
                  {completionPolicy === 'blocked'
                    ? 'agent cannot complete'
                    : completionPolicy === 'allowed'
                      ? 'agent may complete with evidence'
                      : 'completion policy unspecified'}
                </span>
              </div>

              {(evidenceTags.length > 0 || approverTags.length > 0 || reviewAgentTags.length > 0 || gateTags.length > 0) && (
                <div className="grid grid-cols-1 gap-2 text-xs font-mono">
                  {evidenceTags.length > 0 && (
                    <div>
                      <span className="text-[10px] uppercase text-gray-500">Evidence required</span>
                      <div className="mt-1 flex flex-wrap gap-1">
                        {evidenceTags.map((tag) => (
                          <span key={tag} className="px-1.5 py-0.5 rounded bg-blue-950/30 text-blue-300">{tag.slice('evidence:'.length)}</span>
                        ))}
                      </div>
                    </div>
                  )}
                  {approverTags.length > 0 && (
                    <div>
                      <span className="text-[10px] uppercase text-gray-500">Approver</span>
                      <div className="mt-1 flex flex-wrap gap-1">
                        {approverTags.map((tag) => (
                          <span key={tag} className="px-1.5 py-0.5 rounded bg-rose-950/30 text-rose-300">{tag.slice('approver:'.length)}</span>
                        ))}
                      </div>
                    </div>
                  )}
                  {reviewAgentTags.length > 0 && (
                    <div>
                      <span className="text-[10px] uppercase text-gray-500">Review agent</span>
                      <div className="mt-1 flex flex-wrap gap-1">
                        {reviewAgentTags.map((tag) => (
                          <span key={tag} className="px-1.5 py-0.5 rounded bg-yellow-950/30 text-yellow-300">{tag.slice('review-agent:'.length)}</span>
                        ))}
                      </div>
                    </div>
                  )}
                  {gateTags.length > 0 && (
                    <div>
                      <span className="text-[10px] uppercase text-gray-500">Meaning / risk gate</span>
                      <div className="mt-1 flex flex-wrap gap-1">
                        {gateTags.map((tag) => (
                          <span key={tag} className="px-1.5 py-0.5 rounded bg-purple-950/30 text-purple-300">{tag}</span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              <div className="border-t pt-3 space-y-2" style={{ borderColor: 'var(--theme-surface-tertiary, #222836)' }}>
                <div className="flex items-center gap-1.5 text-[10px] font-mono uppercase" style={{ color: 'var(--theme-text-muted, #8e98ab)' }}>
                  <Bot className="w-3 h-3" />
                  Agent runs &amp; evidence
                  {agentRuns.length > 0 && <span className="text-gray-500">({agentRuns.length})</span>}
                </div>
                {agentRunsLoading && hasWorkflowMetadata && (
                  <div className="flex items-center gap-2 text-xs font-mono text-gray-500">
                    <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading runs...
                  </div>
                )}
                {!agentRunsLoading && agentRunsError && (
                  <div className="text-xs font-mono text-gray-600 italic">
                    Agent run history unavailable: {agentRunsError}
                  </div>
                )}
                {!agentRunsLoading && !agentRunsError && agentRuns.length === 0 && (
                  <div className="text-xs font-mono text-gray-600 italic">
                    No AgentRun rows yet. Agents should create one before claiming work.
                  </div>
                )}
                {prRuns.length > 0 && (
                  <div className="space-y-1">
                    {prRuns.map((run) => (
                      <a
                        key={`${run.id}-pr`}
                        href={run.pr_url ?? '#'}
                        target="_blank"
                        rel="noreferrer"
                        className="flex items-center gap-2 text-xs font-mono text-blue-300 hover:text-blue-200"
                      >
                        <GitPullRequest className="w-3 h-3 shrink-0" />
                        <span className="truncate">{run.pr_url}</span>
                        <ExternalLink className="w-3 h-3 shrink-0" />
                      </a>
                    ))}
                  </div>
                )}
                {evidenceRuns.length > 0 && (
                  <div className="space-y-1">
                    {evidenceRuns.map((run) => (
                      <div key={`${run.id}-evidence`} className="flex items-center gap-2 text-xs font-mono text-gray-400">
                        <FileText className="w-3 h-3 shrink-0 text-cyan-400" />
                        <span className="truncate">{run.evidence_uri}</span>
                        {run.tests_status && <span className="text-[10px] text-gray-500">tests: {run.tests_status}</span>}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Description */}
          {task.description && (
            <div>
              <label className="text-[10px] font-mono uppercase mb-1.5 block" style={{ color: 'var(--theme-text-muted, #8e98ab)' }}>
                Description
              </label>
              <div
                className="text-sm leading-relaxed whitespace-pre-wrap rounded-lg p-3 border"
                style={{
                  backgroundColor: 'var(--theme-surface-tertiary, #222836)',
                  borderColor: 'var(--theme-surface-tertiary, #334155)',
                  color: 'var(--theme-text-secondary, #c2c9d6)',
                }}
              >
                {task.description}
              </div>
            </div>
          )}

          {/* ─── Dependency Graph ─────────────────────── */}
          {(dependsOnTasks.length > 0 || blocksTasks.length > 0) && (
            <div
              className="rounded-lg border p-3.5 space-y-3"
              style={{
                borderColor: 'var(--theme-surface-tertiary, #222836)',
                backgroundColor: 'rgba(0,0,0,0.15)',
              }}
            >
              <label className="text-[10px] font-mono uppercase flex items-center gap-1.5" style={{ color: 'var(--theme-text-muted, #8e98ab)' }}>
                <Link2 className="w-3 h-3" /> Dependencies
              </label>

              {/* Depends on */}
              {dependsOnTasks.length > 0 && (
                <div className="space-y-1.5">
                  <span className="text-[9px] font-mono uppercase text-gray-500">Depends on</span>
                  {dependsOnTasks.map(({ id, task: depTask }) => (
                    <div key={id} className="flex items-center gap-2 pl-2 border-l-2 border-gray-700/50">
                      {depTask ? (
                        <button
                          onClick={() => onSelectTask(depTask)}
                          className="flex items-center gap-2 text-xs font-mono hover:opacity-80 transition-opacity group"
                        >
                          <DepStatusIcon status={depTask.status} />
                          <span style={{ color: 'var(--theme-accent-primary, var(--sp-accent))' }}>{depTask.task_id || id}</span>
                          <span className="text-gray-500 truncate max-w-[200px] group-hover:text-gray-400 transition-colors">
                            {depTask.title}
                          </span>
                        </button>
                      ) : (
                        <span className="text-xs font-mono text-gray-600">{id} (not found)</span>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Blocks */}
              {blocksTasks.length > 0 && (
                <div className="space-y-1.5">
                  <span className="text-[9px] font-mono uppercase text-orange-400/70">Blocks</span>
                  {blocksTasks.map(({ id, task: blockedTask }) => (
                    <div key={id} className="flex items-center gap-2 pl-2 border-l-2 border-orange-700/30">
                      {blockedTask ? (
                        <button
                          onClick={() => onSelectTask(blockedTask)}
                          className="flex items-center gap-2 text-xs font-mono hover:opacity-80 transition-opacity group"
                        >
                          <DepStatusIcon status={blockedTask.status} />
                          <span style={{ color: 'var(--theme-accent-primary, var(--sp-accent))' }}>{blockedTask.task_id || id}</span>
                          <span className="text-gray-500 truncate max-w-[200px] group-hover:text-gray-400 transition-colors">
                            {blockedTask.title}
                          </span>
                        </button>
                      ) : (
                        <span className="text-xs font-mono text-gray-600">{id} (not found)</span>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ─── Governor / Dispatch Ops ──────────────── */}
          {(task.governor_status || task.repo || task.branch) && (
            <div
              className="rounded-lg border p-3.5 space-y-2"
              style={{
                borderColor: 'var(--theme-surface-tertiary, #222836)',
                backgroundColor: 'rgba(0,0,0,0.15)',
              }}
            >
              <label className="text-[10px] font-mono uppercase flex items-center gap-1.5" style={{ color: 'var(--theme-text-muted, #8e98ab)' }}>
                <Shield className="w-3 h-3" /> Dispatch Ops
              </label>
              {task.governor_status && GOVERNOR_ICON[task.governor_status] && (() => {
                const entry = GOVERNOR_ICON[task.governor_status!];
                const GovIcon = entry.Icon;
                return (
                  <div className="flex items-center gap-2 text-xs font-mono">
                    <GovIcon className={`w-4 h-4 ${entry.color}`} />
                    <span className={entry.color}>{entry.label}</span>
                    {typeof task.canon_score === 'number' && (
                      <span className="text-gray-500 ml-1">canon {task.canon_score.toFixed(2)}</span>
                    )}
                  </div>
                );
              })()}
              {task.governor_reason && (
                <div className="text-xs font-mono text-gray-400 pl-6">{task.governor_reason}</div>
              )}
              {(task.repo || task.branch) && (
                <div className="flex items-center gap-2 text-xs font-mono text-gray-400 flex-wrap">
                  {task.repo && (
                    <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-blue-900/20 text-blue-300 border border-blue-800/40 uppercase">
                      {task.repo}
                    </span>
                  )}
                  {task.branch && (
                    <span className="flex items-center gap-1 text-purple-300">
                      <GitBranch className="w-3 h-3" />
                      <span className="truncate max-w-[200px]">{task.branch}</span>
                    </span>
                  )}
                  {task.head_sha && (
                    <span className="text-gray-600 text-[10px]">{task.head_sha.slice(0, 8)}</span>
                  )}
                </div>
              )}
              <div className="flex items-center gap-3 text-[10px] font-mono text-gray-500">
                {task.tests_status && (
                  <span className="flex items-center gap-1">
                    <span className={`inline-block w-1.5 h-1.5 rounded-full ${
                      task.tests_status === 'pass' ? 'bg-green-400' : task.tests_status === 'fail' ? 'bg-red-400' : 'bg-gray-500'
                    }`} />
                    tests: {task.tests_status}
                  </span>
                )}
                {task.verified_on_main && (
                  <span className="text-green-400 flex items-center gap-1">
                    <CheckCircle2 className="w-3 h-3" /> verified
                  </span>
                )}
              </div>
              {task.handoff_path && (
                <div className="flex items-center gap-2 text-xs font-mono">
                  <FileText className="w-3 h-3 text-cyan-400 shrink-0" />
                  <span className="text-cyan-400 truncate">{task.handoff_path}</span>
                </div>
              )}
            </div>
          )}

          {/* ─── Event Timeline ──────────────────────── */}
          {(task.status === 'in_progress' || task.status === 'review' || task.status === 'blocked' || task.harness_workflow_id) && (
            <div
              className="rounded-lg border p-3.5"
              style={{
                borderColor: 'var(--theme-surface-tertiary, #222836)',
                backgroundColor: 'rgba(0,0,0,0.15)',
              }}
            >
              <div className="flex items-center gap-1.5 mb-3">
                <Activity className={`w-3 h-3 ${sseConnected ? 'text-[#39FF14]' : 'text-gray-600'}`} />
                <label className="text-[10px] font-mono uppercase" style={{ color: 'var(--theme-text-muted, #8e98ab)' }}>
                  Event Timeline {sseConnected ? '(live)' : ''}
                </label>
                <span className="text-[10px] font-mono text-gray-600 ml-auto">{events.length} events</span>
              </div>

              {events.length === 0 ? (
                <div className="text-xs font-mono text-gray-600 italic py-2">
                  Waiting for events...
                </div>
              ) : (
                <div className="space-y-0 relative">
                  {/* Vertical line */}
                  <div className="absolute left-[6px] top-1 bottom-1 w-px bg-gray-800" />

                  {events.map((event) => {
                    const evConfig = EVENT_ICON[event.kind] ?? { icon: Circle, color: 'text-gray-500' };
                    const EvIcon = evConfig.icon;
                    return (
                      <div key={event.event_id} className="flex items-start gap-3 py-1.5 relative">
                        <div className={`${evConfig.color} shrink-0 z-10 bg-[var(--theme-surface-secondary)] rounded-full`}>
                          <EvIcon className="w-3.5 h-3.5" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="text-[10px] font-mono text-gray-600 shrink-0">
                              {formatEventTime(event.occurred_at)}
                            </span>
                            <span className={`text-[11px] font-mono font-medium ${evConfig.color}`}>
                              {event.kind.replace('task_', '')}
                            </span>
                          </div>
                          {event.summary && (
                            <p className="text-[11px] font-mono text-gray-500 truncate mt-0.5">
                              {event.summary}
                            </p>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* ─── Comment Thread (real) ──────────────── */}
          <TaskCommentThread taskId={task.id} readOnly={readOnly} />

          {/* Dispatch Prompt — Director ruling 2026-05-12: every task should
              be actionable from the board. The prompt is what an agent (or
              the Director) reads to start work. Show it as a readable block
              with a Copy button rather than a truncated single line. */}
          <DispatchPromptBlock prompt={task.dispatch_prompt} />

          {/* Source linkage — supports two patterns:
              1. `source_doc` field (older pattern; relative path to a doc)
              2. `source_memo:*` tag (newer pattern from memo-hook;
                 path to the sovereign memo or brainstorming doc that
                 created this task) */}
          <SourceLinkBlock
            sourceDoc={task.source_doc}
            tags={task.tags}
          />

          {/* Resolution (if done) */}
          {task.resolution && (
            <div>
              <label className="text-[10px] font-mono text-green-500 uppercase mb-1 block">Resolution</label>
              <div className="text-sm font-mono whitespace-pre-wrap bg-green-900/10 rounded-lg p-3 border border-green-800/30" style={{ color: 'var(--theme-text-secondary, #c2c9d6)' }}>
                {task.resolution}
              </div>
            </div>
          )}

          {/* Timestamps */}
          <div className="text-[10px] font-mono space-y-0.5 pt-3 border-t" style={{ borderColor: 'var(--theme-surface-tertiary, #222836)', color: 'var(--theme-text-muted, #8e98ab)' }}>
            <div>Created: {new Date(task.created_at).toLocaleString()}</div>
            {task.updated_at && <div>Updated: {new Date(task.updated_at).toLocaleString()}</div>}
            {task.completed_at && <div className="text-green-600">Completed: {new Date(task.completed_at).toLocaleString()}</div>}
            {task.harness_workflow_id && (
              <div>Workflow: <span className="text-gray-500">{task.harness_workflow_id}</span></div>
            )}
          </div>
        </div>
      </div>

      {/* Director ruling modal — only mounted when rulingContext exists
          and modal is open. The modal posts to /api/v1/memos/ruling and
          on success triggers onUpdate() so the parent re-fetches the
          task list (the RULING-* task is now done; downstream tasks
          tagged blocked_on:{questionId} have lost that tag). */}
      {rulingContext && (
        <RulingSubmitModal
          open={rulingModalOpen}
          task={task}
          memoPath={rulingContext.memoPath}
          questionId={rulingContext.questionId}
          onClose={() => setRulingModalOpen(false)}
          onSuccess={() => {
            setRulingModalOpen(false);
            onUpdate();
          }}
        />
      )}
    </div>
  );
}
